import React, { useCallback, useEffect, useRef, useState } from 'react';
import { jsonAuthHeaders } from './apiAuth';
import { PASSES_AVANT_PROPOSITION, desactiverTextes, noterLecture, noterPasse }
  from './texteReglage';

// Le texte de fin de série : ce à quoi les mots viennent de servir.
//
// PREMIER TEMPS de la tuile de fin de série, avant le bilan. Empiler texte,
// question et trois options SOUS l'anneau ajoutait ~250 px à une carte qui en
// faisait déjà 500 : sur téléphone, le texte serait né sous le pli. Ici il
// occupe la tuile pour un battement, puis lui rend la place. Même largeur, même
// fond, même filet, même arrondi que le bilan et que la carte de quiz : rien ne
// change de format d'un écran à l'autre.
//
// Les sept formats ont une bonne réponse, et il a fallu s'y reprendre à deux
// fois. Trois d'entre eux posaient d'abord une question sans réponse juste
// (« Тебе знакомо? », « Купишь? », « Что ты ответишь? »), pour demander un geste
// plutôt qu'une performance : la fin de série est une récompense, et y coller un
// examen la transforme en corvée. Mais un choix qui n'engage rien ne donne
// aucune raison de cliquer, et personne ne donne son avis personnel à un site
// qu'il ne connaît pas. Une question de compréhension se répond en ayant lu, ce
// qui est exactement ce qu'on vient de faire.
//
// `juge` reste néanmoins branché sur `reponse` : un texte sans bonne réponse ne
// doit pas s'afficher en faux, et le corpus est du contenu, pas du code.
//
// Le texte, la question et les options restent EN RUSSE et ne se traduisent
// jamais : c'est ce qui rend la fonctionnalité indolore pour les cinq domaines.

export default function TexteFinal({ texte, lang, userId, play, stopAudio, onDone }) {
  const t = lang.ui.texte;
  const [choisi, setChoisi] = useState(null);
  const [proposeArret, setProposeArret] = useState(false);
  const juge = Boolean(texte.reponse);
  // Une seule trace par texte affiché, quoi qu'il arrive ensuite : répondre puis
  // fermer ne doit pas écrire deux lignes, et Échap pendant l'envoi non plus.
  const envoyeRef = useRef(false);

  // L'audio est servi en Cache-Control immuable : sans empreinte dans l'URL, un
  // texte corrigé continuerait de jouer l'ancien son. Même casse-cache que la
  // compréhension orale. `audio` peut manquer (corpus généré par lots, synthèse
  // ensuite) : le bouton disparaît alors, il ne casse rien.
  const audioSrc = texte.audio
    ? `/static/${texte.audio}${texte.audio_hash ? `?v=${texte.audio_hash.slice(0, 8)}` : ''}`
    : null;

  const tracer = useCallback((chosen, passe) => {
    if (envoyeRef.current || !userId) return;
    envoyeRef.current = true;
    // La justesse n'est PAS envoyée : le serveur la recalcule depuis le corpus.
    // Une mesure que l'écran décide ne mesure plus rien.
    fetch('/api/texte_reponse', {
      method: 'POST',
      headers: jsonAuthHeaders(),
      body: JSON.stringify({ user_id: userId, texte_id: texte.id, chosen, passe }),
    }).catch(() => { /* la trace est une mesure, jamais un obstacle */ });
  }, [userId, texte.id]);

  const continuer = useCallback(() => { noterLecture(); onDone(); }, [onDone]);

  const fermer = useCallback(() => {
    // La proposition d'arrêt est déjà à l'écran : la fermer, c'est répondre
    // « non ». Sans ce cas, Échap repassait par le comptage et réaffichait la
    // proposition indéfiniment, donc on ne pouvait plus en sortir au clavier.
    if (proposeArret) { continuer(); return; }
    // Fermer sans avoir touché aux options, c'est passer. On le compte, et au
    // bout de trois on propose d'arrêter les frais plutôt que de reposer la
    // même question toutes les dix questions.
    if (choisi === null) {
      tracer(null, true);
      if (noterPasse() >= PASSES_AVANT_PROPOSITION) { setProposeArret(true); return; }
    }
    onDone();
  }, [choisi, tracer, onDone, proposeArret, continuer]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') fermer(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fermer]);

  // Le même garde-fou que sur les cartes du quiz, et pour la même raison.
  // La dernière question de la série vient de prononcer sa phrase d'exemple au
  // panneau de révélation : sans cette coupure, on lit un texte pendant qu'une
  // voix dit tout autre chose. Et au démontage, l'audio du texte ne doit pas se
  // poursuivre par-dessus le bilan puis la carte suivante.
  useEffect(() => {
    stopAudio?.();
    return () => stopAudio?.();
  }, [stopAudio]);

  const repondre = (o) => {
    if (choisi !== null) return;
    setChoisi(o);
    tracer(o, false);
    noterLecture();
  };

  const arreter = () => { desactiverTextes(); onDone(); };

  // Le texte, coupé aux endroits que le serveur a repérés. Surligner les mots de
  // la série vaut mieux que l'annoncer par une phrase : on comprend d'un coup
  // d'œil ce que ce texte a à voir avec la série qu'on vient de faire, sans rien
  // avoir à lire ni à croire. `whitespace-pre-line` reste sur le paragraphe,
  // donc les sauts de ligne des dialogues survivent au découpage.
  const morceaux = () => {
    const spans = texte.surligne || [];
    if (!spans.length) return texte.texte;
    const out = [];
    let prev = 0;
    spans.forEach(([a, b], i) => {
      if (a > prev) out.push(texte.texte.slice(prev, a));
      out.push(
        <mark key={i} className="bg-blue-400/20 text-blue-100 font-semibold rounded px-0.5">
          {texte.texte.slice(a, b)}
        </mark>
      );
      prev = b;
    });
    if (prev < texte.texte.length) out.push(texte.texte.slice(prev));
    return out;
  };

  const classeOption = (o) => {
    // Avant le choix : neutre. Le vert et l'ambre du quiz veulent dire « juste »
    // et « faux » ; les employer avant qu'on ait répondu, ou sur un texte sans
    // bonne réponse, mentirait sur ce qu'on vient de faire.
    if (choisi === null || !juge) {
      return choisi === o
        ? 'bg-slate-700 border-slate-500 text-white'
        : 'bg-slate-900/60 border-slate-700 text-slate-200 hover:border-slate-500';
    }
    if (o === texte.reponse) return 'bg-green-500/15 border-green-500/60 text-green-300';
    if (o === choisi) return 'bg-amber-500/10 border-amber-500/50 text-amber-300';
    return 'bg-slate-900/60 border-slate-800 text-slate-600';
  };

  // ── Renoncement durable ────────────────────────────────────────────────────
  // Proposé une seule fois, après trois passages d'affilée, et il faut le
  // demander : le défaut reste de montrer les textes. Se rouvre depuis le
  // panneau de compte, jamais définitif.
  if (proposeArret) {
    return (
      <div className="bg-slate-800 border border-slate-700/50 rounded-2xl px-5 md:px-7 py-7 w-full flex flex-col gap-4 shadow-xl">
        <p className="text-sm text-slate-300">{t.optOutPrompt}</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={arreter}
            className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold bg-slate-700 hover:bg-slate-600 text-slate-100 transition-colors"
          >
            {t.optOutYes}
          </button>
          <button
            onClick={continuer}
            className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-500 text-white transition-colors"
          >
            {t.optOutNo}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 border border-slate-700/50 rounded-2xl px-5 md:px-7 py-7 w-full flex flex-col gap-4 shadow-xl animate-fade-in-up motion-reduce:animate-none">
      {/* Le « Passer » vit ICI, en haut, et non sous les trois options : il était
          sous le pli sur téléphone, donc il fallait faire défiler tout ce qu'on
          voulait justement éviter pour pouvoir l'éviter. Échap fait la même
          chose au clavier. */}
      <div className="flex items-center justify-between gap-3 w-full">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{t.eyebrow}</span>
          <span className="text-[11px] font-medium text-slate-600 truncate">{t.formats[texte.format] || ''}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {audioSrc && (
            <button
              onClick={() => play(audioSrc)}
              title={t.listen}
              aria-label={t.listen}
              className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 hover:bg-blue-500 active:scale-95 transition-all"
            >
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-white">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
              </svg>
            </button>
          )}
          <button
            onClick={fermer}
            className="text-xs text-slate-500 hover:text-slate-200 transition-colors px-1 py-1"
          >
            {t.skip}
          </button>
        </div>
      </div>

      {/* Le texte, la vedette. Bleu clair et interligne large, comme la phrase
          d'exemple au dos de la carte de quiz : même rôle, même traitement. */}
      <p lang="ru" className="text-[15px] sm:text-base text-blue-200 leading-relaxed break-words whitespace-pre-line">
        {morceaux()}
      </p>

      {texte.question && (
        <p lang="ru" className="text-sm font-semibold text-slate-200 break-words">
          {texte.question}
        </p>
      )}

      <div className="flex flex-col gap-1.5">
        {(texte.options || []).map((o) => (
          <button
            key={o}
            lang="ru"
            onClick={() => repondre(o)}
            disabled={choisi !== null}
            className={`px-4 py-2.5 rounded-xl text-sm font-medium border text-left transition-all break-words ${classeOption(o)} ${
              choisi !== null ? 'cursor-default' : 'cursor-pointer active:scale-[0.98]'
            }`}
          >
            {o}
          </button>
        ))}
      </div>

      {/* Le bouton du bas n'apparaît qu'une fois le choix fait : tant qu'on n'a
          pas répondu, « Passer » en haut suffit, et deux sorties simultanées
          poseraient une question de plus au moment d'en poser une. */}
      {choisi !== null && (
        <button
          onClick={onDone}
          className="self-center px-6 py-2.5 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-500 text-white transition-colors"
        >
          {t.next}
        </button>
      )}
    </div>
  );
}
